import { useEffect, useState } from "react";
import { API, rupiah } from "@/lib/api";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Printer } from "lucide-react";

const COLORS = ["#0B1F3A", "#C9A227", "#1971c2", "#2f9e44", "#e8590c", "#862e9c", "#c2255c"];

const TABS = [
  { v: "ringkasan", l: "Ringkasan" },
  { v: "jurnal", l: "Jurnal Umum" },
  { v: "buku", l: "Buku Besar" },
  { v: "labarugi", l: "Laba Rugi" },
  { v: "neraca", l: "Posisi Keuangan (Neraca)" },
  { v: "aruskas", l: "Arus Kas" },
];

const ACCT_LABEL = { kas: "Kas", piutang: "Piutang", hutang: "Hutang", modal: "Modal" };

export default function Reports() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("ringkasan");
  const today = new Date().toISOString().slice(0, 10);
  const [periode, setPeriode] = useState("bulanan");
  const [tgl, setTgl] = useState(today);
  const [bln, setBln] = useState(today.slice(0, 7));
  const [thn, setThn] = useState(String(new Date().getFullYear()));
  const [detail, setDetail] = useState(null);

  const range = () => {
    if (periode === "harian") return { frm: tgl, to: tgl };
    if (periode === "bulanan") {
      const [y, m] = bln.split("-").map(Number);
      const last = new Date(y, m, 0).getDate();
      return { frm: `${bln}-01`, to: `${bln}-${String(last).padStart(2, "0")}` };
    }
    return { frm: `${thn}-01-01`, to: `${thn}-12-31` };
  };

  useEffect(() => {
    API.get("/reports/summary").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === "ringkasan") return;
    const { frm, to } = range();
    const url = tab === "jurnal" ? "/accounting/jurnal" : tab === "buku" ? "/accounting/buku-besar" : tab === "labarugi" ? "/accounting/laba-rugi" : tab === "neraca" ? "/accounting/neraca" : "/accounting/arus-kas";
    setDetail(null);
    API.get(url, { params: { frm, to } }).then((r) => setDetail(r.data)).catch(() => setDetail(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, periode, tgl, bln, thn]);

  if (!data) return <p className="text-slate-500">Memuat laporan...</p>;

  const { frm, to } = range();
  const periodeLabel = periode === "harian" ? `Tanggal ${tgl}` : periode === "bulanan" ? `Bulan ${bln}` : `Tahun ${thn}`;
  const inputCls = "bg-white border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]";

  return (
    <div className="space-y-6" data-testid="admin-reports">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0B1F3A]">Laporan Bisnis</h1>
          <p className="text-sm text-slate-500">Jurnal umum, buku besar, laba rugi, neraca &amp; arus kas — otomatis, bisa dicetak PDF</p>
        </div>
        {tab !== "ringkasan" && (
          <button onClick={() => window.print()} data-testid="print-report" className="no-print inline-flex items-center gap-2 px-5 py-2.5 bg-[#0B1F3A] text-white text-sm font-bold rounded-md">
            <Printer className="h-4 w-4" /> Cetak / PDF
          </button>
        )}
      </div>

      <div className="no-print flex flex-wrap gap-2" data-testid="report-tabs">
        {TABS.map((t) => (
          <button key={t.v} onClick={() => { setTab(t.v); setDetail(null); }} data-testid={`tab-${t.v}`} className={`px-4 py-2 text-xs font-bold rounded-full border transition-colors ${tab === t.v ? "bg-[#0B1F3A] text-white border-[#0B1F3A]" : "border-slate-300 text-slate-500 hover:border-[#0B1F3A]"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab !== "ringkasan" && (
        <div className="no-print flex flex-wrap items-end gap-3 bg-white border border-slate-200 rounded-md p-4" data-testid="period-picker">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Periode</label>
            <select value={periode} onChange={(e) => setPeriode(e.target.value)} data-testid="periode-select" className={inputCls}>
              <option value="harian">Harian</option>
              <option value="bulanan">Bulanan</option>
              <option value="tahunan">Tahunan</option>
            </select>
          </div>
          {periode === "harian" && (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Tanggal</label>
              <input type="date" value={tgl} onChange={(e) => setTgl(e.target.value)} data-testid="periode-date" className={inputCls} />
            </div>
          )}
          {periode === "bulanan" && (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Bulan</label>
              <input type="month" value={bln} onChange={(e) => setBln(e.target.value)} data-testid="periode-month" className={inputCls} />
            </div>
          )}
          {periode === "tahunan" && (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Tahun</label>
              <input type="number" min="2020" max="2100" value={thn} onChange={(e) => setThn(e.target.value)} data-testid="periode-year" className={inputCls} />
            </div>
          )}
          <p className="text-xs text-slate-400 pb-2">Menampilkan: <strong>{periodeLabel}</strong> ({frm} s.d. {to})</p>
        </div>
      )}

      {tab !== "ringkasan" && (
        <div className="print-area bg-white border border-slate-200 rounded-md p-6" data-testid="report-detail">
          <div className="border-b-2 border-[#0B1F3A] pb-3 mb-5">
            <p className="font-display font-bold text-lg text-[#0B1F3A]">NAWADAH by NDH Official</p>
            <p className="text-xs text-slate-500">{TABS.find((t) => t.v === tab)?.l} — {periodeLabel} ({frm} s.d. {to})</p>
          </div>
          {!detail ? (
            <p className="text-slate-400 text-sm">Memuat...</p>
          ) : tab === "jurnal" ? (
            <table className="w-full text-sm" data-testid="jurnal-table">
              <thead>
                <tr className="border-b border-slate-300 text-left text-xs uppercase tracking-widest text-slate-400">
                  <th className="py-2">Tanggal</th><th className="py-2">Akun</th><th className="py-2">Keterangan</th><th className="py-2 text-right">Debit</th><th className="py-2 text-right">Kredit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {detail.map((e) => (
                  <tr key={e.id}>
                    <td className="py-2 text-xs text-slate-500">{e.date}</td>
                    <td className="py-2 font-semibold">{ACCT_LABEL[e.account] || e.account}</td>
                    <td className="py-2 text-slate-600">{e.description}</td>
                    <td className="py-2 text-right">{e.direction === "debit" ? rupiah(e.amount) : "-"}</td>
                    <td className="py-2 text-right">{e.direction === "kredit" ? rupiah(e.amount) : "-"}</td>
                  </tr>
                ))}
                {detail.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">Tidak ada entri pada periode ini.</td></tr>}
              </tbody>
            </table>
          ) : tab === "buku" ? (
            <div className="space-y-8" data-testid="buku-besar">
              {detail.map((acc) => (
                <div key={acc.account}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold">{ACCT_LABEL[acc.account]}</h3>
                    <p className="text-xs text-slate-500">Saldo awal: <strong>{rupiah(acc.saldo_awal)}</strong></p>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-300 text-left text-xs uppercase tracking-widest text-slate-400">
                        <th className="py-2">Tanggal</th><th className="py-2">Keterangan</th><th className="py-2 text-right">Debit</th><th className="py-2 text-right">Kredit</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {acc.rows.map((e) => (
                        <tr key={e.id}>
                          <td className="py-1.5 text-xs text-slate-500">{e.date}</td>
                          <td className="py-1.5 text-slate-600">{e.description}</td>
                          <td className="py-1.5 text-right">{e.direction === "debit" ? rupiah(e.amount) : "-"}</td>
                          <td className="py-1.5 text-right">{e.direction === "kredit" ? rupiah(e.amount) : "-"}</td>
                        </tr>
                      ))}
                      {acc.rows.length === 0 && <tr><td colSpan={4} className="py-3 text-center text-slate-400 text-xs">Tidak ada mutasi.</td></tr>}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-slate-300 font-bold">
                        <td className="py-2" colSpan={2}>Mutasi / Saldo Akhir</td>
                        <td className="py-2 text-right">{rupiah(acc.debit)}</td>
                        <td className="py-2 text-right">{rupiah(acc.kredit)}</td>
                      </tr>
                      <tr className="font-bold text-[#0B1F3A]">
                        <td className="py-1" colSpan={2}>Saldo Akhir {ACCT_LABEL[acc.account]}</td>
                        <td className="py-1 text-right text-base" colSpan={2}>{rupiah(acc.saldo_akhir)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ))}
            </div>
          ) : tab === "labarugi" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="laba-rugi">
              <div>
                <h3 className="font-bold mb-3 text-emerald-700">Pendapatan</h3>
                <div className="space-y-2 text-sm">
                  {detail.pendapatan.map((r) => (
                    <div key={r.category} className="flex justify-between"><span className="text-slate-600">{r.category}</span><span className="font-semibold">{rupiah(r.total)}</span></div>
                  ))}
                  {detail.pendapatan.length === 0 && <p className="text-slate-400 text-xs">Tidak ada pendapatan.</p>}
                  <div className="flex justify-between border-t border-slate-300 pt-2 font-bold"><span>Total Pendapatan</span><span>{rupiah(detail.total_pendapatan)}</span></div>
                </div>
              </div>
              <div>
                <h3 className="font-bold mb-3 text-red-600">Beban / Pengeluaran</h3>
                <div className="space-y-2 text-sm">
                  {detail.beban.map((r) => (
                    <div key={r.category} className="flex justify-between"><span className="text-slate-600">{r.category}</span><span className="font-semibold">{rupiah(r.total)}</span></div>
                  ))}
                  {detail.beban.length === 0 && <p className="text-slate-400 text-xs">Tidak ada beban.</p>}
                  <div className="flex justify-between border-t border-slate-300 pt-2 font-bold"><span>Total Beban</span><span>{rupiah(detail.total_beban)}</span></div>
                </div>
              </div>
              <div className="md:col-span-2 bg-[#0B1F3A] rounded-md p-4 flex justify-between items-center">
                <span className="text-slate-300 font-bold text-sm uppercase tracking-widest">Laba Bersih</span>
                <span className={`font-display text-2xl font-bold ${detail.laba >= 0 ? "text-[#C9A227]" : "text-red-400"}`}>{rupiah(detail.laba)}</span>
              </div>
            </div>
          ) : tab === "neraca" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="neraca">
              <div>
                <h3 className="font-bold mb-3">Aset</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-600">Kas</span><span className="font-semibold">{rupiah(detail.kas)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">Piutang Usaha</span><span className="font-semibold">{rupiah(detail.piutang)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">Persediaan Barang (nilai jual)</span><span className="font-semibold">{rupiah(detail.persediaan)}</span></div>
                  <div className="flex justify-between border-t border-slate-300 pt-2 font-bold"><span>Total Aset</span><span>{rupiah(detail.aset_total)}</span></div>
                </div>
              </div>
              <div>
                <h3 className="font-bold mb-3">Kewajiban &amp; Ekuitas</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-600">Hutang</span><span className="font-semibold">{rupiah(detail.hutang)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">Modal</span><span className="font-semibold">{rupiah(detail.modal)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">Laba Ditahan</span><span className="font-semibold">{rupiah(detail.laba_ditahan)}</span></div>
                  <div className="flex justify-between border-t border-slate-300 pt-2 font-bold"><span>Total Kewajiban + Ekuitas</span><span>{rupiah(detail.pasiva_total)}</span></div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6" data-testid="arus-kas">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-bold mb-3 text-emerald-700">Kas Masuk</h3>
                  <div className="space-y-2 text-sm">
                    {detail.masuk.map((r) => (
                      <div key={r.category} className="flex justify-between"><span className="text-slate-600">{r.category}</span><span className="font-semibold">{rupiah(r.total)}</span></div>
                    ))}
                    {detail.masuk.length === 0 && <p className="text-slate-400 text-xs">Tidak ada kas masuk.</p>}
                    <div className="flex justify-between border-t border-slate-300 pt-2 font-bold"><span>Total Kas Masuk</span><span>{rupiah(detail.total_masuk)}</span></div>
                  </div>
                </div>
                <div>
                  <h3 className="font-bold mb-3 text-red-600">Kas Keluar</h3>
                  <div className="space-y-2 text-sm">
                    {detail.keluar.map((r) => (
                      <div key={r.category} className="flex justify-between"><span className="text-slate-600">{r.category}</span><span className="font-semibold">{rupiah(r.total)}</span></div>
                    ))}
                    {detail.keluar.length === 0 && <p className="text-slate-400 text-xs">Tidak ada kas keluar.</p>}
                    <div className="flex justify-between border-t border-slate-300 pt-2 font-bold"><span>Total Kas Keluar</span><span>{rupiah(detail.total_keluar)}</span></div>
                  </div>
                </div>
              </div>
              <div className="space-y-2 text-sm border-t border-slate-200 pt-4">
                <div className="flex justify-between"><span className="text-slate-500">Saldo Kas Awal Periode</span><span className="font-semibold">{rupiah(detail.saldo_awal)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Kenaikan/Penurunan Kas Bersih</span><span className="font-semibold">{rupiah(detail.net)}</span></div>
                <div className="flex justify-between font-bold text-base bg-[#0B1F3A] text-[#C9A227] rounded-md px-4 py-3"><span>Saldo Kas Akhir</span><span>{rupiah(detail.saldo_akhir)}</span></div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "ringkasan" && (
      <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="rep-penjualan">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Total Penjualan (Selesai)</p>
          <p className="text-xl font-bold text-[#0B1F3A]">{rupiah(data.penjualan_total)}</p>
          <p className="text-xs text-slate-400 mt-1">{data.pesanan_selesai} pesanan selesai</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="rep-pengeluaran">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Total Pengeluaran</p>
          <p className="text-xl font-bold text-red-600">{rupiah(data.laba_rugi.keluar)}</p>
        </div>
        <div className="bg-[#0B1F3A] rounded-md p-5" data-testid="rep-laba">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-300 mb-2">Laba Bersih (Kas)</p>
          <p className={`text-xl font-bold ${data.laba_rugi.laba >= 0 ? "text-[#C9A227]" : "text-red-400"}`}>{rupiah(data.laba_rugi.laba)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <h2 className="font-bold text-sm mb-4">Penjualan Harian (30 Hari Terakhir)</h2>
          <div className="h-64" data-testid="chart-sales">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.sales_by_day}>
                <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={4} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => (v >= 1000000 ? `${v / 1000000}jt` : v >= 1000 ? `${v / 1000}rb` : v)} />
                <Tooltip formatter={(v) => rupiah(v)} />
                <Bar dataKey="total" fill="#0B1F3A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-5">
          <h2 className="font-bold text-sm mb-4">Laba-Rugi Bulanan (6 Bulan)</h2>
          <div className="h-64" data-testid="chart-cash">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.cash_by_month}>
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => (v >= 1000000 ? `${v / 1000000}jt` : `${v / 1000}rb`)} />
                <Tooltip formatter={(v) => rupiah(v)} />
                <Legend />
                <Line type="monotone" dataKey="masuk" name="Kas Masuk" stroke="#2f9e44" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="keluar" name="Kas Keluar" stroke="#e03131" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="laba" name="Laba" stroke="#C9A227" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-5">
          <h2 className="font-bold text-sm mb-4">Status Pesanan</h2>
          <div className="h-64" data-testid="chart-status">
            {data.order_status.length === 0 ? (
              <p className="text-sm text-slate-400 pt-20 text-center">Belum ada pesanan.</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.order_status} dataKey="count" nameKey="status" innerRadius={50} outerRadius={90} label={({ name, value }) => `${name}: ${value}`} fontSize={10}>
                    {data.order_status.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-5">
          <h2 className="font-bold text-sm mb-4">Produk Terlaris</h2>
          <div className="space-y-3" data-testid="top-products">
            {data.top_products.length === 0 && <p className="text-sm text-slate-400">Belum ada penjualan.</p>}
            {data.top_products.map((p, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="h-7 w-7 rounded-full bg-[#0B1F3A] text-[#C9A227] text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{p.name}</p>
                  <p className="text-xs text-slate-400">{p.qty} terjual</p>
                </div>
                <p className="text-sm font-bold text-[#0B1F3A]">{rupiah(p.total)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      </div>
      )}
    </div>
  );
}
